import styles from "../../styles/applicationDetail.module.css";
import type { PropertyTabProps } from "../../types/tabs";
import { usePropertyTab } from "../../hooks/usePropertyTab";
import { InviteLookupModal } from "../modals/InviteLookupModal";
import {
  CoResidentViewSection,
  LeasingAgentViewSection,
  PropertyViewSection,
  resolveCoResidentViewOtherIndividuals,
} from "./property/PropertyViewSections";
import {
  isExplicitNonPrimaryLayer,
  resolvePropertyCoResidentsForDisplay,
  resolveReadOnlyCoResidents,
} from "./propertyTabHelpers";
import {
  PropertyCoResidentEditSection,
  PropertyLeasingAgentEditSection,
} from "./property/PropertyEditSections";
import { PropertyEditDialogs } from "./property/PropertyEditDialogs";
import { PropertyMovingToEditSection } from "./property/PropertyMovingToEditSection";

export function PropertyTab(props: PropertyTabProps) {
  const tab = usePropertyTab(props);

  const isCoApplicantView = isExplicitNonPrimaryLayer(tab.applicationData);
  const readOnlyCoResidents = resolveReadOnlyCoResidents(
    tab.applicationAllData,
    tab.applicationData,
  );

  if (tab.isEdit) {
    return (
      <div
        className={`${styles.tabContent} ${styles.propertyTabPremium} ${styles.propertyEditPremium}`}
        data-testid="application-property-tab-edit-page"
      >
        <PropertyMovingToEditSection
          formData={tab.formData}
          property={tab.property}
          selectedPropertyData={tab.selectedPropertyData}
          displayInviteCode={tab.displayInviteCode}
          lookingUpInvite={tab.lookingUpInvite}
          validatingInvite={tab.validatingInvite}
          propertyOptions={tab.propertyOptions}
          propertyOptionsLoading={tab.propertyOptionsLoading}
          propertyTabStates={tab.propertyTabStates}
          leaseDurationOptions={tab.leaseDurationOptions}
          hasContractData={tab.hasContractData}
          showRentLeaseReason={tab.showRentLeaseReason}
          showNotWorkingAlert={tab.showPropertyNotWorkingAlert}
          isAdvanceSearchOpen={tab.isAdvanceSearchOpen}
          advanceSearchSelectedId={tab.advanceSearchSelectedId}
          advanceSearchState={tab.advanceSearchState}
          advanceSearchCity={tab.advanceSearchCity}
          advanceSearchStates={tab.advanceSearchStates}
          advanceSearchCities={tab.advanceSearchCities}
          advanceSearchProperties={tab.advanceSearchProperties}
          advanceSearchLoadingStates={tab.advanceSearchLoadingStates}
          advanceSearchLoadingProperties={tab.advanceSearchLoadingProperties}
          propertyInlineMessage={tab.propertyInlineMessage}
          onInputChange={tab.handleInputChange}
          onInviteLookup={tab.handleInviteLookup}
          onValidateInvite={tab.handleValidateInvite}
          onOpenAdvanceSearch={tab.handleOpenAdvanceSearch}
          onCloseAdvanceSearch={() => tab.setIsAdvanceSearchOpen(false)}
          onApplyAdvanceSearch={tab.handleApplyAdvanceSearch}
          onAdvanceSearchStateChange={tab.handleAdvanceSearchStateChange}
          onAdvanceSearchCityChange={tab.handleAdvanceSearchCityChange}
          onAdvanceSearchPropertyChange={tab.setAdvanceSearchSelectedId}
          onPropertySelect={(propertyId) => tab.applySelectedProperty(propertyId)}
          onLoadProperties={tab.loadProperties}
          onRentAmountFocus={tab.handleRentAmountFocus}
          onRentAmountBlur={tab.handleRentAmountBlur}
          onLeaseStartBlur={tab.handleLeaseStartBlur}
          onOpenRentChangePopup={tab.onOpenRentChangePopup}
        />

        {tab.showLeasingAgentSection && (
          <PropertyLeasingAgentEditSection
            leasingConsultantLabel={tab.leasingConsultantLabel}
            loadingAgents={tab.loadingAgents}
            agentOptions={tab.agentOptions}
            formData={tab.formData}
            propertyInlineMessage={tab.propertyInlineMessage}
            onAgentSelection={tab.applyAgentSelection}
            onInputChange={tab.handleInputChange}
            onAgentPhoneInput={tab.handleAgentPhoneInput}
          />
        )}

        <PropertyCoResidentEditSection
          formData={tab.formData}
          inviteRelaxedCoResident={tab.inviteRelaxedCoResident}
          resendInviteLoading={tab.resendInviteLoading}
          coResidentFieldMsg={tab.coResidentFieldMsg}
          onInputChange={tab.handleInputChange}
          onCoResidentChange={tab.handleCoResidentChange}
          onCoResidentPhoneInput={tab.handleCoResidentPhoneInput}
          onAddCoResident={tab.addCoResident}
          onRemoveCoResident={tab.removeCoResident}
          onRequestResendInvite={(inviteCode) =>
            tab.setResendInviteConfirm({ inviteCode })
          }
          isCoApplicantView={isCoApplicantView}
          readOnlyCoResidents={readOnlyCoResidents}
        />

        <PropertyEditDialogs
          rentAmountConfirm={tab.rentAmountConfirm}
          leaseLeadConfirm={tab.leaseLeadConfirm}
          resendInviteConfirm={tab.resendInviteConfirm}
          resendInviteLoading={tab.resendInviteLoading}
          inviteExpiredConfirm={tab.inviteExpiredConfirm}
          onClearRentAmountConfirm={() => tab.setRentAmountConfirm(null)}
          onClearLeaseLeadConfirm={() => tab.setLeaseLeadConfirm(null)}
          onResetLeaseStartAfterLeadWarning={tab.resetLeaseStartAfterLeadWarning}
          onResendInvite={() => void tab.handleResendInvite()}
          onClearResendInviteConfirm={() => tab.setResendInviteConfirm(null)}
          onConfirmInviteExpired={tab.confirmInviteExpired}
          onClearInviteExpiredConfirm={() => tab.setInviteExpiredConfirm(null)}
        />

        <InviteLookupModal
          isOpen={tab.inviteLookupOpen}
          invites={tab.inviteLookupResults}
          onClose={() => tab.setInviteLookupOpen(false)}
          onSelect={tab.handleInviteSelected}
        />
      </div>
    );
  }

  return (
    <div
      className={`${styles.tabContent} ${styles.propertyTabPremium}`}
      data-testid="application-property-tab-page"
    >
      <PropertyViewSection
        applicationAllData={tab.applicationAllData}
        property={tab.property}
        displayInviteCode={tab.displayInviteCode}
        showNotWorkingAlert={tab.showPropertyNotWorkingAlert}
        onOpenProperty={tab.openPropertyInReact}
      />

      <LeasingAgentViewSection
        hasViewLeasingInfo={tab.hasViewLeasingInfo}
        leasingConsultantLabel={tab.agentLabels.leasingConsultantLabel}
        viewLeasing={tab.viewLeasing}
      />

      <CoResidentViewSection
        otherIndividuals={
          resolveCoResidentViewOtherIndividuals(
            tab.applicationAllData,
            tab.applicationData,
            resolvePropertyCoResidentsForDisplay(
              tab.applicationAllData,
              tab.applicationData,
            ),
          )
            ? "1"
            : "0"
        }
      />

      <InviteLookupModal
        isOpen={tab.inviteLookupOpen}
        invites={tab.inviteLookupResults}
        onClose={() => tab.setInviteLookupOpen(false)}
        onSelect={tab.handleInviteSelected}
      />
    </div>
  );
}
